import cloneDeep from 'lodash/cloneDeep';
import type { LevelData } from '../level/LevelData';
import { BoardModel } from '../boardMVC/models/board/BoardModel';
import { TrayModel } from '../boardMVC/models/tray/TrayModel';
import { LevelTasksTracker } from '../tasks/LevelTasksTracker';
import { TRAY_SETTINGS } from '../scene/GameSceneConstants';
import { extractLevelRefillColors } from '../level/utils/extractLevelRefillColors';
import { connectTaskTrackerToBoard } from '../tasks/connectTaskTrackerToBoard';
import { GameEndFlowController } from '../GameEndFlowController';
import type { SolverHistory, SolverHistoryStep, SolverResults, SolverStrategy, StrategyInput } from './types';

export class Solver {
  public readonly taskTracker: LevelTasksTracker;

  public readonly boardModel: BoardModel;

  private readonly trayModel: TrayModel;

  private readonly endFlowController: GameEndFlowController;

  private resolvedState: boolean | null = null;

  private failedDueToMovesLimit = false;

  private readonly history: SolverHistoryStep[] = [];

  constructor(
    private readonly strategy: SolverStrategy,
    private readonly levelData: LevelData,
    private readonly label = '',
  ) {
    this.taskTracker = new LevelTasksTracker(levelData, {});
    this.boardModel = new BoardModel({
      state: levelData.board,
    });
    this.trayModel = new TrayModel(
      {
        boardModel: this.boardModel,
        ...TRAY_SETTINGS,
        refills: cloneDeep(levelData.refills),
        refillColors: extractLevelRefillColors(levelData),
      },
      levelData.tray,
    );

    this.boardModel.onScoreUpdated(this.taskTracker.getHexTally());
    this.trayModel.updateProgress(this.taskTracker.getCombinedProgress());
    this.trayModel.refillItems(false);

    connectTaskTrackerToBoard({
      boardModel: this.boardModel,
      boardView: null,
      levelTaskTracker: this.taskTracker,
      trayModel: this.trayModel,
    });

    this.endFlowController = new GameEndFlowController({
      boardModel: this.boardModel,
      taskTracker: this.taskTracker,
    });
  }

  public getStrategyLabel(): string {
    return this.strategy.label;
  }

  public start(): SolverResults {
    let movesMade = 0;
    while (this.resolvedState === null) {
      this.makeMove();
      movesMade += 1;

      if (movesMade >= 300) {
        this.failedDueToMovesLimit = true;
        this.resolvedState = false;
      }

      if (this.endFlowController.checkLevelWon()) {
        this.resolvedState = true;
      } else if (this.endFlowController.checkOutOfSpace()) {
        this.resolvedState = false;
      }
    }

    this.boardModel.destroy();

    return {
      movesMade,
      resolved: this.resolvedState,
      progress: this.taskTracker.getTaskTallies(),
      failedDueToMovesLimit: this.failedDueToMovesLimit,
    };
  }

  public makeMove(): void {
    const input: StrategyInput = {
      levelData: this.levelData,
      boardModel: this.boardModel,
      trayModel: this.trayModel,
      taskTracker: this.taskTracker,
    };

    const move = this.strategy.getNextMove(input);

    this.history.push({
      boardModel: this.boardModel.exportState(),
      trayModel: this.trayModel.exportState(),
      move,
      progress: this.taskTracker.getAllTaskProgress(),
    });

    const fromModel = this.trayModel.getItems()[move.fromStack];
    if (!fromModel) {
      throw new Error('Cannot find from model');
    }

    const targetStack = this.boardModel.getStack(move.toStack.x, move.toStack.y);
    if (!targetStack) {
      throw new Error('Cannot find target stack');
    }

    this.boardModel.makeMove(targetStack.id, fromModel.removeAllTiles());

    const shouldRefillTray = !this.trayModel.hasActiveTiles();
    if (shouldRefillTray) {
      this.trayModel.refillItems();
    }

    let i = 0;
    while (!this.boardModel.isIdle()) {
      if (i > 3000) {
        console.error(`${this.label} - Solver move iteration above threshold`, this);
        this.resolvedState = false;
        return;
      }
      this.boardModel.update(100);
      i += 1;
    }

    this.boardModel.onScoreUpdated(this.taskTracker.getHexTally());
  }

  public getHistory(): SolverHistory {
    return {
      resolved: this.resolvedState ?? false,
      steps: this.history,
    };
  }
}
